import { useEffect, useMemo, useRef, useState } from 'react';

import { useDisplayPixelRatio } from '../../useDisplayPixelRatio';
import { useIsOffline } from '../../useIsOffline';
import {
  BestPhotoSizeScanDirection,
  ORIGINAL_SIZE,
  PhotoSize,
  SCALED_PHOTO_SIZES,
  ScaledPhotoSize,
} from '../Photos/PhotoGalleryContext';
import { usePhotoGallery } from './PhotoGalleryContext';

// TODO: (usePhotoUri/renovator/UX/kaiPro) prefetch a "which photoIds are fetchable right now" list so the UI can render
//   intentionally (e.g. pending/uploading vs. available) instead of firing speculative
//   fetches that 404.
// IDEA: Maybe use kaiProApiClient.getScanPhotosById per batch of photos in the UX's photo gallery. If any are missing, long-poll on that call, but not individual photo calls.

/**
 * The usePhoto* hooks: every component that draws a scan photo resolves it through one of these,
 * for its own photoId, and never calls the gallery's getPhotoUri directly. Each hook gives the
 * component all of the following.
 *
 * Sharp images, sized to the screen:
 * - usePhotoUriForLayoutSize asks for the smallest cached size (256, 512, 1024, 2048) that covers the
 *   layout size times the display pixel ratio.
 * - The ratio comes from KaiProApiStateProvider: the browser's real devicePixelRatio, or what the
 *   platform's init reports, defaulting to 2x. A 1x monitor gets half the pixels.
 * - Zoomable full-screen viewers use usePhotoFullSizeUri, which always asks for 2048.
 *
 * Fast first paint:
 * - A cached photo renders on the first render, read synchronously from the file cache's hot
 *   mirror, so there is no flicker.
 * - Otherwise the best smaller cached size shows at once and swaps to the requested size when it
 *   lands.
 * - If the network fails, the lower-res copy stays on screen: something beats nothing.
 *
 * Less network:
 * - A smaller size is scaled locally from a larger cached copy rather than downloaded.
 * - Concurrent requests for the same photo and size share one in-flight request.
 * - Downloads run through a concurrency-limited queue, with separate limits for browser and native.
 * - Photos persist across sessions in an LRU file cache; probing for the best local size does not
 *   disturb the eviction order.
 *
 * Resilient:
 * - Transient download failures retry with backoff.
 * - A 404 (a photo the server has not finished processing, say one uploaded from another device)
 *   subscribes to the realtime channel and fetches again when a push says it is available, for
 *   up to two minutes, with no blind polling.
 * - Offline, the hook shows the best local copy and makes no request that cannot succeed.
 * - Coming back online, a photo still missing its requested size retries automatically.
 * - Final fallbacks: a cached original, then an original captured on this device and not yet synced.
 *
 * One implementation everywhere: the hooks run on the platform's file system (an in-memory one
 * serving blob URLs on web, device storage on React Native), so renovator and kaiPro behave alike.
 * The loading, caching and retry machinery lives in GalleryPhotoLoader.
 *
 * usePhotoUri is the general form: it resolves `photoId` at `size`, searching cached sizes in
 * `scanDirection` for the preview. Prefer usePhotoUriForLayoutSize or usePhotoFullSizeUri.
 */
export const usePhotoUri = (photoId: string | undefined, size: PhotoSize, scanDirection?: BestPhotoSizeScanDirection): string | undefined => {
  const { getPhotoUri, getBestLocallyAvailablePhoto, getBestLocallyAvailablePhotoSync } = usePhotoGallery();
  const isOffline = useIsOffline();

  // Optimistically render getBestLocallyAvailablePhotoSync if available, otherwise undefined.
  // The initializer MUST NOT throw — if it does, useState fails to register and the next
  // render reports "Rendered more hooks than during the previous render". Wrap defensively.
  const [uri, setUri] = useState<string | undefined>(() => {
    if (!photoId) return undefined;
    try {
      return getBestLocallyAvailablePhotoSync?.(photoId, size, scanDirection)?.uri;
    } catch {
      return undefined;
    }
  });

  // True once the URI on screen is the actually-requested size (exact local hit or successful fetch).
  // Gates the offline→online retry: if we already have the right size, don't re-fetch.
  const hasRequestedSizeRef = useRef(false);

  // Reset the "have requested size" flag whenever we switch what we're resolving.
  useEffect(() => {
    hasRequestedSizeRef.current = false;
  }, [photoId, size, scanDirection]);

  useEffect(() => {
    if (!photoId) {
      setUri(undefined);
      return;
    }
    if (hasRequestedSizeRef.current) return;

    let cancelled = false;
    const fetch = async () => {
      try {
        const cachedFallbackUri = await getBestLocallyAvailablePhoto(photoId, size, scanDirection);
        if (cancelled) return;
        if (cachedFallbackUri) {
          setUri(cachedFallbackUri.uri);
          if (cachedFallbackUri.size === size) {
            hasRequestedSizeRef.current = true;
            return;
          }
        }
        const canGetPhoto = !isOffline || await getBestLocallyAvailablePhoto(photoId, size, 'higherResolution');
        if (!canGetPhoto) return; // no network and no higher resolution available; keep lower-res fallback, if any, on screen and wait for online to retry
        const fetchedRequestedSizeUri = await getPhotoUri(photoId, size);
        if (cancelled) return;
        if (fetchedRequestedSizeUri) {
          setUri(fetchedRequestedSizeUri);
          hasRequestedSizeRef.current = true;
        }
      } catch {
        // ignore — best-effort is good enough
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [photoId, size, scanDirection, getPhotoUri, getBestLocallyAvailablePhoto, isOffline]);

  return uri;
};

/** The smallest cached size whose longest side covers `devicePixels`; 2048 for anything larger. */
export const getScaledPhotoSizeForDevicePixels = (devicePixels: number): ScaledPhotoSize =>
  SCALED_PHOTO_SIZES.find(size => size >= devicePixels) ?? SCALED_PHOTO_SIZES[SCALED_PHOTO_SIZES.length - 1];

/**
 * The hook to use for tiles, strips and thumbnails: resolves a photo sized for the space it is drawn in. `layoutSizePx` is how many layout (CSS) pixels
 * the photo's longest side must fill; it is scaled by the device's pixel ratio to pick the smallest
 * cached size that stays sharp. For a `cover` crop, pass the side the photo's long edge spans after
 * cropping (for a 4:3 photo in a square tile, the tile side × 4/3).
 */
export const usePhotoUriForLayoutSize = (photoId: string | undefined, layoutSizePx: number): string | undefined =>
  usePhotoUri(photoId, getScaledPhotoSizeForDevicePixels(layoutSizePx * useDisplayPixelRatio()));

/** Always the 256px size. Prefer usePhotoUriForLayoutSize, which follows the display pixel ratio. */
export const usePhotoThumbnailUri = (photoId: string | undefined): string | undefined => usePhotoUri(photoId, 256);

/** Always the 512px size. Prefer usePhotoUriForLayoutSize, which follows the display pixel ratio. */
export const usePhotoMediumSizeUri = (photoId: string | undefined): string | undefined => usePhotoUri(photoId, 512);

/** Always the 1024px size. Prefer usePhotoUriForLayoutSize, which follows the display pixel ratio. */
export const usePhotoLargeSizeUri = (photoId: string | undefined): string | undefined => usePhotoUri(photoId, 1024);

/** The 2048px size, for zoomable full-screen viewers; the preview may come from a larger or smaller cached size. */
export const usePhotoFullSizeUri = (photoId: string | undefined): string | undefined => usePhotoUri(photoId, 2048, 'both');

/** The original, full-resolution source photo. */
export const usePhotoMaxSizeUri = (photoId: string | undefined): string | undefined => usePhotoUri(photoId, ORIGINAL_SIZE);

// load all photos, full-sized - primarily for offline preloading purposes
export const usePhotoUris = (photoIds: string[]) => {
  const { getPhotoUri } = usePhotoGallery();
  const [loadedUris, setLoadedUris] = useState<Record<string, string>>({});
  const [completedCount, setCompletedCount] = useState(0);
  const prevPhotoIdsKeyRef = useRef<string>('');

  const photoIdsKey = photoIds.join(',');

  // Reset state when photoIds change
  if (photoIdsKey !== prevPhotoIdsKeyRef.current) {
    prevPhotoIdsKeyRef.current = photoIdsKey;
    setLoadedUris({});
    setCompletedCount(0);
  }

  useEffect(() => {
    if (photoIds.length === 0) return;

    let cancelled = false;

    const fetchAll = async () => {
      for (const photoId of photoIds) {
        if (cancelled) return;
        try {
          const uri = await getPhotoUri(photoId);
          if (!cancelled && uri) {
            setLoadedUris(prev => ({ ...prev, [photoId]: uri }));
          }
        } catch {
          // failed URIs are not added to loadedUris
        }
        if (!cancelled) {
          setCompletedCount(prev => prev + 1);
        }
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [photoIdsKey, getPhotoUri]);

  const loadedUrisArray = useMemo(
    () => photoIds.filter(id => loadedUris[id]).map(id => loadedUris[id]),
    [photoIds, loadedUris],
  );

  const total = photoIds.length;

  return {
    loadedUris: loadedUrisArray,
    loadingPhotoIds: [] as string[],
    pendingPhotoIds: [] as string[],
    isReady: total === 0 || completedCount >= total,
    isLoading: total > 0 && completedCount < total,

    /** 0-1 progress */
    progress: total > 0 ? completedCount / total : 1,
  };
};
